/**
 * PhoneShell — mobilní shell ACM.
 *
 * - 1 sloupec (data-layout=phone + grid lock)
 * - Start: StartPage (servery + menu + moje plocha)
 * - App bar: 4 kontextová tlačítka (Start | předchozí | nastavení | akce)
 */
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  Columns2,
  LayoutDashboard,
  LayoutGrid,
  Monitor,
  Network,
  Plus,
  RefreshCw,
  Server,
  Settings,
  UserCircle,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { NodeSwitch } from './NodeSwitch';
import { NotificationBell } from './SidebarNotifications';
import { api } from '../lib/api';
import { RUNNING_UI_BUILD } from '../lib/build';
import { hardReloadUi } from '../lib/pwa';
import {
  peekPhoneNavPrevious,
  pushPhoneNav,
  useUiLayout,
} from '../lib/ui-layout';
import { isPhoneCodableWorkFocus } from '../lib/phone-sections';
import { uiScope, uiScopeHome } from '../lib/ui-scope';
import { cn } from '../lib/utils';
import {
  effectiveStartItems,
  getStartCatalog,
  loadStartMenu,
  type StartMenuItem,
} from '../lib/start-menu';

function isStartRoute(pathname: string): boolean {
  return pathname === '/start' || pathname === '/phone' || pathname === '/phone/';
}

function matchTile(path: string): StartMenuItem | null {
  const items = [...effectiveStartItems(loadStartMenu()), ...getStartCatalog()];
  const seen = new Set<string>();
  for (const x of items) {
    if (seen.has(x.id)) continue;
    seen.add(x.id);
    if (path === x.to || (x.to !== '/' && path.startsWith(x.to + '/'))) return x;
  }
  return null;
}

function pageTitle(pathname: string, t: (k: string, d: string) => string): string {
  if (pathname.startsWith('/phone/codable/') && pathname !== '/phone/codable') {
    return t('layout.phoneCodableProject', 'Projekt');
  }
  if (pathname.startsWith('/phone/')) {
    const slug = pathname.split('/')[2] ?? '';
    if (slug === 'dashboard') return t('layout.phoneDash', 'Přehled');
    if (slug === 'codable') return t('layout.phoneCodable', 'Codable');
    if (slug === 'node') return t('layout.phoneNode', 'Nod');
    if (slug === 'orchestrator') return t('layout.phoneOrch', 'Orchestrator');
    if (slug === 'wiki') return t('nav.wiki', 'Wiki');
    if (slug === 'containers') return t('nav.containers', 'Kontejnery');
    if (slug === 'proxy') return t('nav.proxy', 'Proxy');
  }
  const hit = matchTile(pathname);
  if (hit) return t(`nav.${hit.tkey}`, hit.label);
  if (pathname.startsWith('/infra/nodes')) return t('infra.nodeWorkspace', 'Nod');
  if (pathname.startsWith('/modules/')) {
    const name = pathname.split('/')[2] ?? '';
    return name || t('nav.modules', 'Moduly');
  }
  if (pathname.startsWith('/core/')) {
    const name = pathname.split('/')[2] ?? '';
    if (name === 'dashboard') return t('nav.dashboard', 'Dashboard');
    return name || 'ACM';
  }
  return uiScope() ? t('nav.codable', 'Codable') : 'ACM';
}

interface AppBarSlot {
  key: string;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  accent?: boolean;
  active?: boolean;
}

function PhoneUiReloadBanner() {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['server-info'],
    queryFn: () =>
      api.mcp<{ ui_build?: string }>('acm-self.ui_context'),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const mismatch = !!data?.ui_build && !!RUNNING_UI_BUILD &&
    data.ui_build !== RUNNING_UI_BUILD;
  if (!mismatch) return null;
  return (
    <button
      type='button'
      className='phone-ui-reload-banner'
      onClick={() => void hardReloadUi()}
    >
      <RefreshCw size={14} strokeWidth={2} />
      <span className='min-w-0 flex-1 truncate text-left'>
        {t('notifications.newBuild', 'Nová verze rozhraní je k dispozici')}
      </span>
      <span className='shrink-0 font-semibold'>
        {t('notifications.reload', 'Znovu načíst')}
      </span>
    </button>
  );
}

export default function PhoneShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const start = isStartRoute(location.pathname);
  const path = location.pathname;
  const title = useMemo(() => pageTitle(path, t), [path, t]);

  const { togglePhoneDesktop } = useUiLayout();

  useEffect(() => {
    pushPhoneNav(path + (location.search || ''));
  }, [path, location.search]);

  const previous = peekPhoneNavPrevious(path);
  const prevTile = previous ? matchTile(previous.split('?')[0] ?? previous) : null;
  const workFocus = isPhoneCodableWorkFocus(path, location.search);

  const appBar: AppBarSlot[] = useMemo(() => {
    const slots: AppBarSlot[] = [];

    slots.push({
      key: 'start',
      label: t('layout.phoneStart', 'Start'),
      icon: LayoutDashboard,
      onClick: () => navigate(uiScope() ? uiScopeHome(uiScope()!, true) : '/start'),
      active: start,
    });

    if (previous && prevTile) {
      slots.push({
        key: 'prev',
        label: t(`nav.${prevTile.tkey}`, prevTile.label),
        icon: LayoutGrid,
        onClick: () => navigate(previous),
      });
    } else if (!start) {
      slots.push({
        key: 'prev',
        label: uiScope()
          ? t('nav.codable', 'Codable')
          : t('nav.dashboard', 'Dashboard'),
        icon: LayoutGrid,
        onClick: () =>
          navigate(uiScope() ? uiScopeHome(uiScope()!, true) : '/core/dashboard'),
      });
    } else {
      slots.push({
        key: 'prev',
        label: uiScope()
          ? t('nav.codable', 'Codable')
          : t('layout.phoneNode', 'Nod'),
        icon: Server,
        onClick: () =>
          navigate(uiScope() ? uiScopeHome(uiScope()!, true) : '/phone/node'),
      });
    }

    const mod = path.match(/^\/(?:core|modules)\/([^/]+)/)?.[1];
    if (mod && mod !== 'dashboard' && mod !== 'panel') {
      slots.push({
        key: 'settings',
        label: t('nav.settings', 'Nastavení'),
        icon: Settings,
        onClick: () => {
          if (mod === 'codable') navigate('/modules/codable/settings');
          else navigate(`/profile`);
        },
      });
    } else if (path.startsWith('/phone/codable/')) {
      slots.push({
        key: 'settings',
        label: t('nav.settings', 'Nastavení'),
        icon: Settings,
        onClick: () => {
          const id = path.split('/')[3];
          if (id) navigate(`/phone/codable/${id}?tab=settings`);
          else navigate('/modules/codable/settings');
        },
      });
    } else {
      slots.push({
        key: 'settings',
        label: t('nav.profile', 'Profil'),
        icon: UserCircle,
        onClick: () => navigate('/profile'),
      });
    }

    if (path === '/phone/codable') {
      slots.push({
        key: 'action',
        label: t('phone.codable.new', 'Nový'),
        icon: Plus,
        accent: true,
        onClick: () => navigate('/modules/codable/new'),
      });
    } else if (path.startsWith('/phone/codable/')) {
      const id = path.split('/')[3];
      slots.push({
        key: 'action',
        label: t('phone.codable.tabWork', 'Práce'),
        icon: Columns2,
        accent: true,
        onClick: () => {
          if (id) navigate(`/phone/codable/${id}?tab=work`);
        },
      });
    } else if (path.startsWith('/phone/node')) {
      slots.push({
        key: 'action',
        label: t('phone.node.open', 'Workspace'),
        icon: Server,
        accent: true,
        onClick: () => navigate('/infra/nodes/local'),
      });
    } else if (path.startsWith('/phone/orchestrator')) {
      slots.push({
        key: 'action',
        label: t('common.refresh', 'Obnovit'),
        icon: RefreshCw,
        accent: true,
        onClick: () => {
          void qc.invalidateQueries({ queryKey: ['phone-orch-list'] });
          void qc.invalidateQueries({ queryKey: ['phone-orch-status'] });
        },
      });
    } else if (path.startsWith('/phone/containers')) {
      slots.push({
        key: 'action',
        label: t('common.refresh', 'Obnovit'),
        icon: RefreshCw,
        accent: true,
        onClick: () => {
          void qc.invalidateQueries({ queryKey: ['phone-docker-containers'] });
          void qc.invalidateQueries({ queryKey: ['phone-docker-images'] });
          void qc.invalidateQueries({ queryKey: ['phone-docker-volumes'] });
          void qc.invalidateQueries({ queryKey: ['phone-docker-networks'] });
          void qc.invalidateQueries({ queryKey: ['phone-docker-logs'] });
        },
      });
    } else if (path.startsWith('/phone/proxy')) {
      slots.push({
        key: 'action',
        label: t('common.refresh', 'Obnovit'),
        icon: RefreshCw,
        accent: true,
        onClick: () => {
          void qc.invalidateQueries({ queryKey: ['phone-npm-hosts'] });
          void qc.invalidateQueries({ queryKey: ['phone-npm-stream'] });
          void qc.invalidateQueries({ queryKey: ['phone-npm-certs'] });
          void qc.invalidateQueries({ queryKey: ['phone-npm-edge'] });
        },
      });
    } else if (path.startsWith('/core/dashboard') || start) {
      slots.push({
        key: 'action',
        label: t('nav.infrastructure', 'Infra'),
        icon: Network,
        accent: true,
        onClick: () => navigate('/core/mesh'),
      });
    } else {
      slots.push({
        key: 'action',
        label: t('common.refresh', 'Obnovit'),
        icon: RefreshCw,
        accent: true,
        onClick: () => {
          void qc.invalidateQueries();
        },
      });
    }

    return slots.slice(0, 4);
  }, [path, previous, prevTile, start, navigate, t, qc]);

  return (
    <div
      className={cn(
        'phone-shell h-screen flex flex-col overflow-hidden',
        workFocus && 'is-work-focus',
      )}
    >
      <PhoneUiReloadBanner />
      {!workFocus && (
        <header className='phone-status shrink-0 flex items-center gap-2 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2'>
          <Zap size={15} className='phone-brand-icon shrink-0' fill='currentColor' />
          <NodeSwitch variant='phone' />
          <div className='ml-auto flex items-center gap-1 shrink-0'>
            <button
              type='button'
              className='phone-header-switch'
              onClick={() => void hardReloadUi()}
              title={t('pwa.reloadApp', 'Obnovit aplikaci')}
              aria-label={t('pwa.reloadApp', 'Obnovit aplikaci')}
            >
              <RefreshCw size={15} strokeWidth={1.75} />
            </button>
            <NotificationBell variant='phone' />
            <button
              type='button'
              className='phone-header-switch'
              onClick={togglePhoneDesktop}
              title={t('layout.switchDesktop', 'Přepnout na desktop')}
              aria-label={t('layout.switchDesktop', 'Přepnout na desktop')}
            >
              <Monitor size={15} strokeWidth={1.75} />
            </button>
          </div>
        </header>
      )}

      {start
        ? (
          <div className='flex-1 overflow-y-auto px-4 pb-28 min-h-0'>
            <Outlet />
          </div>
        )
        : (
          <div className='flex-1 flex flex-col min-h-0'>
            {!workFocus && (
              <div className='px-4 pb-2 shrink-0'>
                <button
                  type='button'
                  onClick={() => {
                    if (path.startsWith('/phone/codable/') && path !== '/phone/codable') {
                      navigate('/phone/codable');
                      return;
                    }
                    if (previous) {
                      navigate(previous);
                      return;
                    }
                    navigate('/start');
                  }}
                  className='inline-flex items-center gap-1 text-[11px] uppercase tracking-widest phone-muted hover:opacity-100 opacity-70 mb-1'
                >
                  <ChevronLeft size={12} />
                  {path.startsWith('/phone/codable/') && path !== '/phone/codable'
                    ? t('layout.phoneCodable', 'Codable')
                    : previous && prevTile
                    ? prevTile.label
                    : t('layout.phoneStart', 'Start')}
                </button>
                <h1 className='phone-title phone-title-sm truncate'>{title}</h1>
              </div>
            )}
            <div
              className={cn(
                'flex-1 min-h-0 phone-page-body',
                workFocus ? 'overflow-hidden is-work-focus' : 'overflow-y-auto',
              )}
            >
              <Outlet />
            </div>
          </div>
        )}

      {!workFocus && (
        <nav className='phone-appbar shrink-0' aria-label='Phone app bar'>
          {appBar.map((slot) => {
            const Icon = slot.icon;
            return (
              <button
                key={slot.key}
                type='button'
                className={[
                  'phone-appbar-btn',
                  slot.accent ? 'phone-appbar-btn-accent' : '',
                  slot.active ? 'phone-appbar-btn-active' : '',
                ].filter(Boolean).join(' ')}
                onClick={slot.onClick}
              >
                <Icon size={18} strokeWidth={1.75} />
                <span className='truncate max-w-full'>{slot.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
